import React from 'react';
import { AREA_COLORS, getUsedColors, isColorAvailable } from '../../config/areaColors';

/**
 * AreaIdentityFields — presentational name/short-code/colour editor for a `user_areas` row.
 *
 * Extracted 2026-08-26 (create-new-home build) from `RegisterLocationStep.jsx`'s inline identity
 * box, which was the first of what `docs/reuse-backlog.md`'s 2026-08-26 entry predicted would
 * become a third duplicate site — this build (the change-home crosshair, `ChangeHomeAreaStep.jsx`)
 * is that third site. Deliberately "dumb": no translation keys, no auto/custom-value logic, no
 * fetches. The caller owns all of that, INCLUDING which translation keys to pass in for the label/
 * placeholder props — as of the save-and-exit/parity build's addendum 1 (2026-08-26), all three
 * callers (RegisterLocationStep, ChangeHomeAreaStep, MyLocations) pass the SAME shared
 * `user.locations_form_area_*` keys (MyLocations' own, pre-existing, live ones — the wizard's
 * separate `auth.wizard_identity_*` set never actually existed on staging, and change-home's own
 * `user.change_home_identity_*` set is now unreferenced but left live). Each caller still has
 * different semantics for what "custom" means (the wizard treats any resumed value as frozen;
 * change-home compares against what the current boundary would auto-derive, so a genuine re-target
 * can still re-derive a name the user never actually customised — see ChangeHomeAreaStep.jsx).
 * Keeping that auto/custom logic out of this component is what lets both callers differ safely.
 *
 * @param {string} displayName
 * @param {string} areaCode
 * @param {string|null} colorHex
 * @param {function} onDisplayNameChange - (value: string) => void
 * @param {function} onAreaCodeChange - (value: string) => void — caller decides normalisation (the
 *   existing callers both uppercase); this component passes the raw input value through.
 * @param {function} onColorHexChange - (hex: string) => void
 * @param {string} nameLabel
 * @param {string} namePlaceholder
 * @param {string} codeLabel
 * @param {string} codePlaceholder
 * @param {string} colorLabel
 * @param {Array} [userAreas] - 2026-08-26 redesign+create-path build (probe report §4/§6, gap
 *   3.4): the caller's other saved areas, used to grey out colours already in use elsewhere —
 *   ports AreaColorPicker's getUsedColors/isColorAvailable (the modal/quick-edit swatch grid
 *   already had this; this inline grid didn't). Omit to render every swatch identically
 *   (pre-existing behaviour, unchanged for any caller that doesn't pass it).
 * @param {React.ReactNode} [afterNameField] - 2026-08-29 UI layout follow-on
 *   (docs/briefs/2026-08-29-mylocations-ui-layout-followon.md): optional content rendered between
 *   the Name and Code fields — MyLocations' only current use is a centred Set-Home button.
 *   Rendering is gated on the prop itself (not just its content), so RegisterLocationStep/
 *   ChangeHomeAreaStep, which don't pass it, get zero extra DOM nodes and no extra flex `gap`
 *   (Advisor-caught: `boxStyle`'s `gap: 12px` applies between all flex children regardless of
 *   whether one is visually empty — an unconditionally-rendered wrapper around a null child would
 *   have silently added a 12px gap to both of those other callers).
 */
const AreaIdentityFields = ({
  displayName,
  areaCode,
  colorHex,
  onDisplayNameChange,
  onAreaCodeChange,
  onColorHexChange,
  nameLabel,
  namePlaceholder,
  codeLabel,
  codePlaceholder,
  colorLabel,
  userAreas,
  afterNameField = null
}) => {
  const boxStyle = { display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px', padding: '12px', background: '#F9FAFB', borderRadius: '10px', border: '1px solid #E5E7EB' };
  const labelStyle = { display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#374151', fontFamily: 'OpenSauceSans, sans-serif' };
  const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: '2px solid #E5E7EB', fontSize: '14px', fontFamily: 'OpenSauceSans, sans-serif' };
  const usedColors = getUsedColors(userAreas);

  return (
    <div style={boxStyle}>
      <div>
        <label style={labelStyle}>{nameLabel}</label>
        <input
          type="text"
          value={displayName}
          onChange={(e) => onDisplayNameChange(e.target.value)}
          placeholder={namePlaceholder}
          style={inputStyle}
        />
      </div>
      {afterNameField ? <div>{afterNameField}</div> : null}
      <div>
        <label style={labelStyle}>{codeLabel}</label>
        <input
          type="text"
          maxLength={6}
          value={areaCode}
          onChange={(e) => onAreaCodeChange(e.target.value)}
          placeholder={codePlaceholder}
          style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: '1px' }}
        />
      </div>
      <div>
        <label style={labelStyle}>{colorLabel}</label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {AREA_COLORS.map((c) => {
            const isCurrent = colorHex?.toUpperCase() === c.hex.toUpperCase();
            const isUsed = !isColorAvailable(c.hex, usedColors) && !isCurrent;
            return (
              <button
                key={c.id}
                type="button"
                disabled={isUsed}
                onClick={() => { if (!isUsed) onColorHexChange(c.hex); }}
                title={`${c.name}${isUsed ? ' (In use)' : ''}`}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: c.hex,
                  border: isCurrent ? '3px solid #024B99' : '2px solid transparent',
                  cursor: isUsed ? 'not-allowed' : 'pointer',
                  opacity: isUsed ? 0.4 : 1,
                  position: 'relative',
                  padding: 0
                }}
              >
                {isUsed && (
                  <>
                    <div style={{ position: 'absolute', top: '50%', left: '2px', right: '2px', height: '1.5px', background: '#ef4444', transform: 'translateY(-50%) rotate(45deg)', pointerEvents: 'none' }} />
                    <div style={{ position: 'absolute', top: '50%', left: '2px', right: '2px', height: '1.5px', background: '#ef4444', transform: 'translateY(-50%) rotate(-45deg)', pointerEvents: 'none' }} />
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AreaIdentityFields;
